import React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

const RichInfoBadge = ({ text, color = '#047857', size = 16, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" data-testid={testId} style={{
        cursor: 'help', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: size, height: size, borderRadius: '50%',
        background: `${color}22`, border: `1px solid ${color}55`, color,
        fontSize: `${size * 0.035}rem`, fontWeight: 700, flexShrink: 0,
        padding: 0, lineHeight: 1
      }}>?</button>
    </PopoverTrigger>
    <PopoverContent
      side="top" align="center" sideOffset={8}
      className="z-[100] max-w-[300px] border-purple-500/40 bg-[rgba(253, 251, 247,0.97)] text-zinc-300 text-xs leading-relaxed shadow-xl"
    >{text}</PopoverContent>
  </Popover>
);

const SearchMapFilters = ({
  minQualityScore,
  setMinQualityScore,
  selectedDocTypes,
  setSelectedDocTypes,
  documentTypes,
  toggleDocType,
  setShowFilteredResults
}) => {
  return (
    <>
      {/* 1. CONTENT QUALITY SCORE */}
      <div style={{
        marginTop: 15,
        padding: 15,
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(245, 158, 11, 0.12))',
        borderRadius: 10,
        border: '2px solid rgba(16, 185, 129, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ color: '#10b981', margin: 0, fontSize: '1rem', fontWeight: 700 }}>
            <i className="fas fa-star" style={{ marginRight: 6, fontSize: '0.85rem' }} />
            Content Quality Score
          </h4>
          {minQualityScore > 0 && (
            <button
              onClick={() => setMinQualityScore(0)}
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
              data-testid="clear-quality-filter"
            >
              Clear Filter
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {[
            { value: 0, label: 'Show All', color: '#a1a1aa', desc: 'No filter' },
            { value: 50, label: 'Good+', color: '#f59e0b', desc: 'Score 50+' },
            { value: 65, label: 'High Quality+', color: '#3b82f6', desc: 'Score 65+' },
            { value: 80, label: 'Premium Only', color: '#10b981', desc: 'Score 80+' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => {
                setMinQualityScore(option.value);
                if (option.value > 0) setShowFilteredResults(true);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '10px 16px',
                background: minQualityScore === option.value
                  ? `${option.color}20`
                  : '#475569',
                border: minQualityScore === option.value
                  ? `2px solid ${option.color}`
                  : '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                cursor: 'pointer',
                transition: 'all 0.2s',
                minWidth: 100
              }}
              data-testid={`quality-filter-${option.value}`}
            >
              <span style={{
                color: minQualityScore === option.value ? option.color : '#e2e8f0',
                fontWeight: minQualityScore === option.value ? 700 : 500,
                fontSize: '0.85rem'
              }}>
                {option.label}
              </span>
              <span style={{
                color: '#71717a',
                fontSize: '0.7rem',
                marginTop: 3
              }}>
                {option.desc}
              </span>
            </button>
          ))}
        </div>
        <p style={{ color: '#71717a', fontSize: '0.72rem', marginTop: 10 }}>
          Filter to show only high-quality, extensively informative content. Premium content has detailed, well-researched information.
        </p>
      </div>

      {/* 2. DOCUMENT TYPE FILTERS */}
      <div style={{
        marginTop: 15,
        padding: 15,
        background: 'rgba(255, 255, 255, 0.4)',
        borderRadius: 10,
        border: '1px solid rgba(252, 211, 77, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h4 style={{ color: '#047857', margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="fas fa-file-alt" style={{ fontSize: '0.8rem' }} />
            Filter by Document Type
            <RichInfoBadge
              text="Filter your search results by content type. Each URL is automatically classified as a specific document type (e.g., Blog Post, Research Paper, Wiki, etc.) during categorization. Check the types you want to see — only matching URLs will appear in your Filtered Results section below."
              testId="doctype-info-tooltip"
            />
          </h4>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                setSelectedDocTypes(documentTypes.map(dt => dt.id));
                setShowFilteredResults(true);
              }}
              style={{
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                color: '#10b981',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
              data-testid="select-all-doctypes"
            >
              Select All
            </button>
            <button
              onClick={() => { setSelectedDocTypes([]); setShowFilteredResults(false); }}
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
              data-testid="deselect-all-doctypes"
            >
              Deselect All
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {documentTypes.map(docType => (
            <label
              key={docType.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                cursor: 'pointer',
                background: selectedDocTypes.includes(docType.id)
                  ? `${docType.color}30`
                  : 'rgba(255, 255, 255, 0.85)',
                border: selectedDocTypes.includes(docType.id)
                  ? `2px solid ${docType.color}`
                  : '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.2s'
              }}
              data-testid={`doctype-${docType.id.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            >
              <input
                type="checkbox"
                checked={selectedDocTypes.includes(docType.id)}
                onChange={() => toggleDocType(docType.id)}
                style={{
                  accentColor: docType.color,
                  width: 14,
                  height: 14
                }}
              />
              <span style={{
                color: selectedDocTypes.includes(docType.id) ? docType.color : '#a1a1aa',
                fontSize: '0.8rem',
                fontWeight: selectedDocTypes.includes(docType.id) ? 600 : 400
              }}>
                {docType.name}
              </span>
            </label>
          ))}
        </div>
        <p style={{ color: '#71717a', fontSize: '0.72rem', marginTop: 10 }}>
          Check document types to filter results. Selected categories and document types combine to show matching results below.
        </p>
      </div>
    </>
  );
};

export default SearchMapFilters;
